// GENERATED — DO NOT HAND-EDIT.
//
// Source of truth: specs/tier-05-experience/asset.entity-hint.yaml
// Generator: tools/v2-gen/v2gen.py
// Regenerate via: make v2-generate-screens
//
// Hand-edits to non-override-block content WILL be lost on next
// regeneration. See specs/_product/dashboard-v2-page-generator.md §5
// for override blocks (imports / helpers / actions / custom-tabs).

import type { DetailPageConfig } from "../../components/composition/DetailPage";
import { assetsApi } from "../../api/assets";
import type { Asset } from "../../api/types";
// override-block:imports BEGIN
import { createElement } from "react";
import type { AssetKind, AssetSource } from "../../api/types";
// CHG-1294 — per-page RBAC gate wiring (consumes GATE_CATALOG).
import { GATE_CATALOG } from "../../auth/gateCatalog";
import { toGate } from "../../auth/toGateProp";
// override-block:imports END

// override-block:helpers BEGIN
const KIND_LABEL: Record<AssetKind, string> = {
  photo: "Photo",
  logo: "Logo",
  illustration: "Illustration",
  pattern: "Pattern",
  video: "Video",
};
const SOURCE_LABEL: Record<AssetSource, string> = {
  uploaded: "Uploaded",
  ai_generated: "AI-generated",
  platform_authentic: "Platform-curated",
};

// Synthetic dimensions string — Asset entity has no pre-formatted
// dimensions field, so the v2-gen status_cards block points its
// value_resolver_fn at this helper (CHG-1314).
function dimensionsLabel(a: Asset): string {
  if (a.kind === "video") {
    return a.duration_seconds != null ? `${a.duration_seconds}s` : "—";
  }
  if (a.width != null && a.height != null) return `${a.width}×${a.height}`;
  return "—";
}

function heroUrl(a: Asset): string | null {
  return (
    a.rendered_sizes?.large ??
    a.rendered_sizes?.medium ??
    a.primary_url ??
    null
  );
}
// override-block:helpers END

export const assetDetailConfig: DetailPageConfig<Asset> = {
  idParam: "assetId",
  queryKey: (id: string) => ["assets", "detail", id] as const,
  queryFn: (id: string) => assetsApi.getAsset(id),
  backTo: "/assets",
  // override-block:title BEGIN
  title: (a) => a.title ?? "(untitled)",
  // override-block:title END
  // override-block:subtitle BEGIN
  subtitle: (a) => `${KIND_LABEL[a.kind] ?? a.kind} · ${SOURCE_LABEL[a.source] ?? a.source}`,
  // override-block:subtitle END
  statusCards: (r, _fmt) => [
    {
      id: "file_size",
      label: "File size",
      value: r.byte_size,
      format: "bytes",
      tone: "g-home",
    },
    {
      id: "dimensions",
      label: "Dimensions",
      value: dimensionsLabel(r),
      format: "text",
      tone: "g-events",
    },
    {
      id: "used_in",
      label: "Used in",
      value: r.used_in_count,
      format: "number",
      tone: "g-giving",
    },
    {
      id: "last_used",
      label: "Last used",
      value: r.last_used_at,
      format: "relative-date",
      tone: "g-mygiving",
    },
  ],
  actions: (_r) => [
    // override-block:actions BEGIN
    { id: "edit", label: "Edit", variant: "secondary", permissionGate: toGate(GATE_CATALOG.asset_detail["edit"], "disable") },
    { id: "replace-version", label: "Replace", variant: "primary", permissionGate: toGate(GATE_CATALOG.asset_detail["replace-version"], "disable") },
    { id: "download", label: "Download", variant: "ghost" },
    // override-block:actions END
  ],
  tabs: [
    // override-block:custom-tabs BEGIN
    {
      id: "overview",
      label: "Overview",
      render: (a, fmt) => {
        const hero = heroUrl(a);
        return createElement(
          "div",
          { className: "card", style: { padding: 16, display: "flex", gap: 24, alignItems: "flex-start" } },
          hero
            ? createElement("img", {
                src: hero,
                alt: a.alt_text ?? a.title ?? "",
                style: {
                  maxWidth: 320,
                  maxHeight: 320,
                  borderRadius: 8,
                  background: "var(--color-surface-2)",
                },
              })
            : null,
          createElement(
            "div",
            { style: { fontSize: 13, lineHeight: 1.6, flex: 1 } },
            createElement("div", null, "Uploaded: ", fmt.formatDate(a.created_at, "long")),
            createElement(
              "div",
              null,
              "Description: ",
              a.description ?? createElement("span", { style: { color: "var(--color-text-muted)" } }, "—"),
            ),
            createElement(
              "div",
              null,
              "Alt text: ",
              a.alt_text ?? createElement("span", { style: { color: "var(--color-text-muted)" } }, "—"),
            ),
            a.is_decorative
              ? createElement(
                  "div",
                  { style: { marginTop: 8, color: "var(--color-text-muted)", fontStyle: "italic" } },
                  "Flagged decorative — screen readers will skip alt text.",
                )
              : null,
          ),
        );
      },
    },
    {
      id: "usage",
      label: "Usage",
      render: (a) =>
        createElement(
          "div",
          { className: "card", style: { padding: 16, color: "var(--color-text-muted)" } },
          `Where this asset is referenced — fetched via GET /assets/${a.id}/usages. ` +
            "Returns AssetUsage rows grouped by consumer_kind (campaigns, donate-pages, " +
            "messages, templates, brand-kits, organizations). Full UI in ε CHG.",
        ),
    },
    {
      id: "versions",
      label: "Versions",
      render: (a) =>
        createElement(
          "div",
          { className: "card", style: { padding: 16, color: "var(--color-text-muted)" } },
          `Version history — fetched via GET /assets/${a.id}/versions. Most-recent-first; ` +
            "monotonic version_number from 1. Full UI in ε CHG.",
        ),
    },
    {
      id: "metadata",
      label: "Metadata",
      render: (a) => {
        const tagList = (a.tags ?? []).join(", ") || "—";
        const traditionList = (a.tradition_tags ?? []).join(", ") || "—";
        return createElement(
          "div",
          { className: "card", style: { padding: 16, fontSize: 13, lineHeight: 1.6 } },
          createElement("div", null, "Tags: ", tagList),
          createElement("div", null, "Tradition tags: ", traditionList),
          createElement("div", null, "License: ", a.license ?? "—"),
          createElement("div", null, "Attribution: ", a.attribution ?? "—"),
          a.source === "ai_generated"
            ? createElement(
                "div",
                { style: { marginTop: 12, padding: 12, background: "var(--color-surface-2)", borderRadius: 6 } },
                createElement("div", { style: { fontWeight: 600, marginBottom: 4 } }, "AI provenance"),
                createElement("div", null, "Model: ", a.ai_model ?? "—"),
                createElement("div", null, "Prompt: ", a.ai_prompt ?? "—"),
                createElement(
                  "div",
                  null,
                  "Requires disclosure: ",
                  a.requires_disclosure ? "yes" : "no",
                ),
              )
            : null,
        );
      },
    },
    // override-block:custom-tabs END
  ],
};
